import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, Icon, ProgressBar } from '@insio/ui-toolkit';
import { BillingModel } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { BillingProvider } from '@/generated/graphql';
import { useExtractionQuota } from '../../hooks/use-extraction-quota';
import type { QuotaInfo } from '../../utils/quota-utils';

interface BillingHeroPanelPlan {
  name: string;
  billingProvider: BillingProvider;
  billingModel: BillingModel;
  maxDocumentsPerMonth: number;
  /** Credits granted each period; null on a documents plan. */
  monthlyCredits: number | null;
  canExceedLimit: boolean;
  amount: string;
  currency: string;
  pricePerExtraDocument: string | null;
}

/** A usage row supplied by the caller, shown in place of the extraction figures. */
interface BillingHeroPanelUsage {
  label: string;
  content: ReactNode;
  /** Rendered as is below the row; it draws its own full-width strip and top border. */
  footer?: ReactNode;
}

interface BillingHeroPanelProps {
  /** Extraction figures; null hides the credits row and leaves the plan and its actions. */
  quota: QuotaInfo | null;
  plan: BillingHeroPanelPlan | null;
  /** Replaces the extraction figures row, e.g. with a credit payer's credits. */
  usage?: BillingHeroPanelUsage;
  /** True when the current subscription can be cancelled by the user (STRIPE plan, no scheduled change/cancellation). */
  showCancelCta?: boolean;
  /** Whether the viewer may manage billing — hides action buttons, keeps the banners. */
  canManage?: boolean;
  /** True when the current subscription is already scheduled to end at period boundary. */
  cancellationScheduled?: boolean;
  /** True when a deferred plan change is scheduled (downgrade). Mutually exclusive with cancellationScheduled. */
  planChangeScheduled?: boolean;
  /** Display name of the plan the subscription will move to at period end. */
  scheduledPlanName?: string | null;
  /** Localized end-of-period date used in the scheduled banner. */
  periodEndLabel?: string | null;
  /** True while a cancel / resume / plan-change mutation is in-flight. */
  mutationLoading?: boolean;
  onCancelClick?: () => void;
  onResumeClick?: () => void;
  onCancelScheduledChange?: () => void;
}

type BarVariant = 'success' | 'warn' | 'error' | 'neutral' | 'primary';

export function BillingHeroPanel({
  quota,
  plan,
  usage,
  showCancelCta = false,
  canManage = true,
  cancellationScheduled = false,
  planChangeScheduled = false,
  scheduledPlanName = null,
  periodEndLabel = null,
  mutationLoading = false,
  onCancelClick,
  onResumeClick,
  onCancelScheduledChange
}: BillingHeroPanelProps) {
  const t = useFeatureTranslation('systemSettings.payments');
  const { i18n } = useTranslation();
  const isFreeProvider = plan?.billingProvider === 'FREE';
  const planAmount = plan ? Number(plan.amount) : 0;
  const showFreeBilling = isFreeProvider || planAmount === 0;

  const isCreditPlan = plan?.billingModel === BillingModel.CREDITS;
  const credits = plan?.monthlyCredits ?? 0;
  // A row keeps its bottom border only when another row follows; strips draw their own.
  const hasUsageRow = !!usage || !!quota;

  return (
    <Card className='gap-0 p-0'>
      <Row label={t('hero.myPlan')}>
        {plan ? (
          <div className='flex flex-wrap items-center gap-sm'>
            <Chip className={'bg-success-bg text-success-text border-success-border'}>
              {plan.name}
            </Chip>
            <span className='text-sm font-semibold text-foreground-base'>
              {isCreditPlan
                ? t('hero.myPlanCredits', {
                    count: credits,
                    credits: credits.toLocaleString(i18n.language)
                  })
                : t('hero.myPlanLimit', { count: plan.maxDocumentsPerMonth })}
            </span>
            {!isCreditPlan && plan.canExceedLimit && (
              <span className='inline-flex items-center gap-xs text-xs text-foreground-muted'>
                <Icon name='circle-check' className='h-3.5 w-3.5 text-success-text' />
                {t('planCard.overageAllowed')}
              </span>
            )}
          </div>
        ) : (
          <span className='text-sm text-foreground-muted'>—</span>
        )}
      </Row>

      <Row label={t('hero.billing')} isLast={!hasUsageRow}>
        {plan ? (
          <span className='text-sm font-semibold text-foreground-base'>
            {showFreeBilling
              ? t('hero.billingFree')
              : t('hero.billingMonthly', {
                  amount: planAmount.toLocaleString(i18n.language),
                  currency: plan.currency
                })}
          </span>
        ) : (
          <span className='text-sm text-foreground-muted'>—</span>
        )}
      </Row>

      {usage ? (
        <>
          <Row label={usage.label} isLast>
            {usage.content}
          </Row>
          {usage.footer}
        </>
      ) : (
        // Without figures (the master account board has no pool quota yet) the plan rows stand alone.
        quota && <CreditsRow quota={quota} plan={plan} />
      )}

      {cancellationScheduled ? (
        <div className='flex flex-col gap-sm bg-warn-bg border-t border-warn-border px-md py-sm sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex items-start gap-sm'>
            <Icon name='triangle-exclamation' className='shrink-0 mt-2xs text-warn-text' />
            <div className='flex flex-col gap-2xs'>
              <span className='text-sm font-semibold text-warn-text'>
                {t('cancelSubscription.scheduled.title')}
              </span>
              {periodEndLabel && (
                <span className='text-xs text-foreground-muted'>
                  {t('cancelSubscription.scheduled.description', { date: periodEndLabel })}
                </span>
              )}
            </div>
          </div>
          {canManage && (
            <Button
              variant='default'
              outline='default'
              size='sm'
              disabled={mutationLoading}
              onClick={onResumeClick}
            >
              {mutationLoading
                ? t('cancelSubscription.processing')
                : t('cancelSubscription.scheduled.resume')}
            </Button>
          )}
        </div>
      ) : planChangeScheduled ? (
        <div className='flex flex-col gap-sm bg-warn-bg border-t border-warn-border px-md py-sm sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex items-start gap-sm'>
            <Icon name='calendar-clock' className='shrink-0 mt-2xs text-warn-text' />
            <div className='flex flex-col gap-2xs'>
              <span className='text-sm font-semibold text-warn-text'>
                {t('scheduledPlanChange.title', { plan: scheduledPlanName ?? '' })}
              </span>
              {periodEndLabel && (
                <span className='text-xs text-foreground-muted'>
                  {t('scheduledPlanChange.description', { date: periodEndLabel })}
                </span>
              )}
            </div>
          </div>
          {canManage && (
            <Button
              variant='default'
              outline='default'
              size='sm'
              disabled={mutationLoading}
              onClick={onCancelScheduledChange}
            >
              {mutationLoading ? t('changePlan.processing') : t('scheduledPlanChange.cancel')}
            </Button>
          )}
        </div>
      ) : showCancelCta && canManage ? (
        <div className='flex items-center justify-end border-t border-border-base px-md py-sm'>
          <Button
            variant='default'
            outline='default'
            size='sm'
            disabled={mutationLoading}
            onClick={onCancelClick}
          >
            {mutationLoading
              ? t('cancelSubscription.processing')
              : t('cancelSubscription.heroButton')}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/** Extractions used in the billing window against the plan, with any overage. */
function CreditsRow({ quota, plan }: { quota: QuotaInfo; plan: BillingHeroPanelPlan | null }) {
  const t = useFeatureTranslation('systemSettings.payments');
  const { pct, isUnlimited, textColorClass, barColorClass, label, resetDate, excess } =
    useExtractionQuota(quota);

  const hasOverage =
    quota.canExceedLimit &&
    quota.limit !== null &&
    quota.hardCap !== null &&
    quota.hardCap > quota.limit;
  const overageCapacity = hasOverage ? (quota.hardCap as number) - (quota.limit as number) : 0;
  const overageUsed = Math.min(Math.max(quota.used - (quota.limit ?? 0), 0), overageCapacity);
  const overagePct = overageCapacity > 0 ? (overageUsed / overageCapacity) * 100 : 0;
  const overageUnitPrice = plan?.pricePerExtraDocument ? Number(plan.pricePerExtraDocument) : 0;
  const overageCost = overageUsed * overageUnitPrice;
  const showOverageCost = hasOverage && overageUnitPrice > 0;

  const barVariant = mapBarColorToVariant(barColorClass);

  return (
    <Row label={t('hero.credits')} isLast>
      <div className='flex w-full flex-col gap-xs'>
        <div className='flex items-baseline justify-between gap-md'>
          <span
            className={`text-sm font-semibold tabular-nums whitespace-nowrap ${textColorClass}`}
          >
            {label}
            {pct !== null && (
              <span className='ml-xs font-normal text-foreground-muted'>({Math.round(pct)} %)</span>
            )}
          </span>
          {hasOverage && (
            <span className='text-xs tabular-nums text-foreground-muted'>
              {t('hero.overageProgress', { used: overageUsed, capacity: overageCapacity })}
              {showOverageCost && (
                <span className='ml-xs'>
                  ·{' '}
                  {t('hero.overageCost', {
                    cost: overageCost.toLocaleString(),
                    currency: plan?.currency ?? ''
                  })}
                </span>
              )}
            </span>
          )}
        </div>
        {!isUnlimited && (
          <div className='flex w-full items-stretch gap-2xs'>
            <div style={{ flex: hasOverage ? (quota.limit ?? 1) : 1 }}>
              <ProgressBar
                value={pct ?? 0}
                variant={barVariant}
                ariaLabel={t('quota.progressAriaLabel', { label })}
              />
            </div>
            {hasOverage && (
              <div style={{ flex: overageCapacity }}>
                <ProgressBar
                  value={overagePct}
                  variant='warn'
                  muted
                  ariaLabel={t('hero.overageAriaLabel')}
                />
              </div>
            )}
          </div>
        )}
        <div className='flex items-center justify-end gap-md text-xs text-foreground-muted'>
          {excess > 0 && <span>{t('quota.excessCount', { count: excess })}</span>}
          <span>{t('quota.resetsOn', { date: resetDate })}</span>
        </div>
      </div>
    </Row>
  );
}

function Row({
  label,
  children,
  isLast
}: {
  label: string;
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-xs px-md py-sm sm:flex-row sm:items-center sm:gap-md ${
        isLast ? '' : 'border-b border-border-base'
      }`}
    >
      <div className='shrink-0 sm:w-40'>
        <span className='text-xs font-medium uppercase tracking-wide text-foreground-muted'>
          {label}
        </span>
      </div>
      <div className='flex-1'>{children}</div>
    </div>
  );
}

function mapBarColorToVariant(barColorClass: string): BarVariant {
  if (barColorClass.includes('error')) return 'error';
  if (barColorClass.includes('warn')) return 'warn';
  if (barColorClass.includes('success')) return 'success';
  return 'neutral';
}
